import axios from 'axios';
import { CONSTANTS } from 'config/constants';
import {
    HISTORICAL_SOURCE_FILE_KINDS,
    HistoricalImportBatch,
    HistoricalSourceFiles,
    StartImportResponse,
    UploadAttachmentsResponse,
} from './historical-import.types';

/**
 * DD-3687: the three calls the historical import makes.
 *
 * Each uses the same session-cookie auth and preflight header as the other
 * super admin imports; `apollo-require-preflight` is what makes the browser
 * send a CORS preflight before the cookie goes out.
 */

const requestOptions = {
    withCredentials: true,
    headers: { 'apollo-require-preflight': '1' },
};

const importUrl = (labId: string) => `${CONSTANTS.REST_API_URI}/organizations/${labId}/uploads/historical-orders`;

/**
 * Phase 1. Hands the export over and gets a batch id back.
 *
 * Up to five files — orders, actions, materials, messages, attachments — each
 * sent under its **kind** rather than its own filename. That looks like a bug
 * and is not: it is the contract the live order import already uses, read back
 * server-side off `file.originalname`.
 *
 * No source system is sent. It comes from the export's own `source_system`
 * column, per order — the dialog used to offer a box for it, which only ever
 * applied to exports that lacked the column and was silently ignored by every
 * export that had one.
 *
 * Returns as soon as the files are stored: an archive is too large to parse
 * inside a request, so the run continues on the server and `fetchImportBatch`
 * reports on it.
 */
export const startHistoricalImport = async (
    labId: string,
    sourceFiles: HistoricalSourceFiles,
): Promise<StartImportResponse> => {
    const formData = new FormData();
    for (const kind of HISTORICAL_SOURCE_FILE_KINDS) {
        const file = sourceFiles[kind];
        if (file) {
            formData.append('files', file, kind);
        }
    }
    const response = await axios.post<StartImportResponse>(importUrl(labId), formData, requestOptions);
    return response.data;
};

/** Phase 2. The run's progress, polled while it works. */
export const fetchImportBatch = async (labId: string, importBatchId: string): Promise<HistoricalImportBatch> => {
    const response = await axios.get<HistoricalImportBatch>(`${importUrl(labId)}/${importBatchId}`, requestOptions);
    return response.data;
};

/**
 * Phase 3. One chunk of attachments.
 *
 * Safe to repeat: the server matches each file to the row the orders import
 * declared and skips one it has already stored, so a chunk that failed
 * half-way can simply be sent again.
 */
export const uploadAttachmentChunk = async (
    labId: string,
    importBatchId: string,
    files: File[],
): Promise<UploadAttachmentsResponse> => {
    const formData = new FormData();
    for (const file of files) {
        formData.append('files', file, file.name);
    }
    const response = await axios.post<UploadAttachmentsResponse>(
        `${importUrl(labId)}/${importBatchId}/attachments`,
        formData,
        requestOptions,
    );
    return response.data;
};

/** The message the server put in `{ error }`, if it sent one. */
export const readRequestError = (error: unknown): string | undefined =>
    axios.isAxiosError(error) ? error.response?.data?.error : undefined;
